/**
 * Folder access, replacing the agent's `?path=` file server and the native
 * Windows folder dialog.
 *
 * The console needs two folders on the event PC: the one the camera's tethering
 * software writes into, and the one finished sessions are filed in. With no
 * agent there is no way to read a path — `showDirectoryPicker()` hands back an
 * opaque handle by design and the page never learns where it points — so the
 * handle IS the setting, stored in IndexedDB and reused on the next launch.
 *
 * Two consequences worth knowing, because they are what the operator sees:
 *
 *  - This is Chromium only. Firefox and Safari have no File System Access API,
 *    which is why `supported()` is a hard requirement rather than letting the
 *    console fail at the moment someone presses "Mulai pantau".
 *  - A stored handle keeps its permission across reloads but NOT across a
 *    browser restart: Chrome downgrades it to "prompt", and re-granting needs a
 *    user gesture. `ensurePermission` is therefore called from a button, never
 *    from an effect. Settings is where it is asked for, once at the start of an
 *    event rather than at the moment a guest is waiting.
 */

import { isSupportedImage } from '@/core/session'
import { KV, get, put } from './db'

export const MONITORED_HANDLE = 'handle:monitored'
export const TARGET_HANDLE = 'handle:target'

export function supported(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window
}

/** Opens the browser's folder picker. Null when the operator cancels. */
export async function pickDirectory(
  id: string,
  mode: 'read' | 'readwrite' = 'read',
): Promise<FileSystemDirectoryHandle | null> {
  if (!supported()) throw new Error('Browser ini tidak mendukung akses folder')
  try {
    // `id` makes Chrome reopen at the folder last chosen for that purpose, so
    // repicking the camera folder does not start from Documents every time.
    return await window.showDirectoryPicker({ id, mode, startIn: 'pictures' })
  } catch (error) {
    // AbortError is a cancelled dialog: an outcome, not a failure. The caller
    // leaves the setting alone, exactly as the agent's 204 meant.
    if (error instanceof DOMException && error.name === 'AbortError') return null
    throw error
  }
}

export async function saveHandle(key: string, handle: FileSystemDirectoryHandle): Promise<void> {
  await put(KV, handle, key)
}

export async function loadHandle(key: string): Promise<FileSystemDirectoryHandle | null> {
  return (await get<FileSystemDirectoryHandle>(KV, key)) ?? null
}

export type PermissionState = 'granted' | 'prompt' | 'denied'

export async function checkPermission(
  handle: FileSystemHandle,
  mode: 'read' | 'readwrite' = 'read',
): Promise<PermissionState> {
  return (await handle.queryPermission({ mode })) as PermissionState
}

/**
 * Re-grants a stored handle. MUST be called from a user gesture — Chrome
 * rejects the prompt otherwise, and a rejection here reads to the operator as
 * "the folder is broken" rather than "click the button".
 */
export async function ensurePermission(
  handle: FileSystemHandle,
  mode: 'read' | 'readwrite' = 'read',
): Promise<boolean> {
  if ((await handle.queryPermission({ mode })) === 'granted') return true
  return (await handle.requestPermission({ mode })) === 'granted'
}

/** A stable, human-readable name for a handle. The closest thing left to a path. */
export function displayName(handle: FileSystemDirectoryHandle | null): string {
  return handle?.name ?? ''
}

/**
 * Creates (or opens) a subfolder. This is how a session folder is made now that
 * there is no agent to call `os.MkdirAll`.
 */
export function subdirectory(
  parent: FileSystemDirectoryHandle,
  name: string,
): Promise<FileSystemDirectoryHandle> {
  return parent.getDirectoryHandle(name, { create: true })
}

/** Writes one file into a folder, replacing it if present. */
export async function writeFile(
  directory: FileSystemDirectoryHandle,
  name: string,
  data: Blob,
): Promise<void> {
  const file = await directory.getFileHandle(name, { create: true })
  const writable = await file.createWritable()
  try {
    await writable.write(data)
  } finally {
    // Closing is what commits the write; skipping it on an error path leaves a
    // zero-byte file in the customer's session folder.
    await writable.close()
  }
}

/**
 * What a chosen folder actually contains.
 *
 * Settings wants to answer "is this the right folder?", and the obvious answer —
 * show the full path — is not available: a `FileSystemDirectoryHandle` exposes
 * `name` and nothing else. There is no path property, and `resolve()` only gives
 * a path relative to an ancestor handle the page already holds. The browser
 * deliberately never tells the page where the folder is.
 *
 * So this answers the question a different way, and arguably a better one: two
 * folders called "photos" are indistinguishable by path at a glance too, but a
 * count and a couple of file names prove the camera's photos are really in
 * there.
 *
 * Bounded, because a real event folder holds several hundred frames and this
 * runs on a settings screen, not a hot path — `limit` caps the walk and
 * `truncated` says the count is a floor rather than a total.
 */
export interface DirectorySummary {
  images: number
  folders: number
  /** A few entry names, for recognising the folder. */
  sample: string[]
  /** The walk hit `limit` and stopped; the counts are "at least". */
  truncated: boolean
}

export async function describeDirectory(
  handle: FileSystemDirectoryHandle,
  { limit = 500, sampleSize = 2 }: { limit?: number; sampleSize?: number } = {},
): Promise<DirectorySummary> {
  const summary: DirectorySummary = { images: 0, folders: 0, sample: [], truncated: false }
  let seen = 0

  for await (const entry of handle.values()) {
    if (seen++ >= limit) {
      summary.truncated = true
      break
    }

    if (entry.kind === 'directory') {
      summary.folders += 1
      continue
    }
    if (!isSupportedImage(entry.name)) continue

    summary.images += 1
    if (summary.sample.length < sampleSize) summary.sample.push(entry.name)
  }

  return summary
}
